import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "../admin/Modal";
import adminStyles from "../admin/Admin.module.css";
import type { BedrockCatalogueModel, SaveBedrockPriceOverride } from "../../api/bedrockPrices";
import { MaxRate } from "./rateInput";
import { fieldNames, MaxSourceLength, toDraft, validateDraft, type Draft, type FieldErrors, type FieldName, type OverrideDialogMode } from "./overrideDraft";
import styles from "./BedrockPrices.module.css";

export type { OverrideDialogMode } from "./overrideDraft";

/** The rate fields, which are number inputs. */
const rateFields: readonly FieldName[] = ["inputPerMillion", "outputPerMillion", "cacheReadPerMillion", "cacheWritePerMillion"];

/**
 * The models the dialog offers, in the order it lists them: those nothing prices first, then the rest, each group by display name. A
 * model that already has an override is left out, as a second one would be refused, unless it is the one being edited.
 */
function offeredModels(models: readonly BedrockCatalogueModel[], editing: string | null): BedrockCatalogueModel[] {
  const byName = (a: BedrockCatalogueModel, b: BedrockCatalogueModel): number => a.displayName.localeCompare(b.displayName, undefined, { sensitivity: "base", numeric: true });
  const free = models.filter((model) => model.overrideId === null || model.modelId === editing);
  return [...free.filter((model) => model.priceSource === "None").sort(byName), ...free.filter((model) => model.priceSource !== "None").sort(byName)];
}

/**
 * The one dialog for creating and editing a Bedrock price override. The model is picked from a drop-down of every model Bedrock offers
 * when that list could be read, and typed otherwise; the rates are number inputs, so they reach the server as plain
 * numbers whatever the person's locale. Fields are checked here the way the server checks them, and a message is shown under each field
 * that fails; nothing is sent until they pass. Whatever the server then answers is told by the page as a toast, and a field it names is
 * marked here too. `onSubmit` answers null when the dialog should close, or the field the server refused.
 */
export function OverrideDialog({
  mode,
  models,
  busy,
  onSubmit,
  onCancel,
  onInvalid,
}: {
  mode: OverrideDialogMode;
  /** Every model Bedrock offers, or null when that list could not be read and the model id is typed. */
  models: readonly BedrockCatalogueModel[] | null;
  busy: boolean;
  onSubmit: (body: SaveBedrockPriceOverride) => Promise<{ field: string | null } | null>;
  onCancel: () => void;
  onInvalid: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<Draft>(() => toDraft(mode));
  const [errors, setErrors] = useState<FieldErrors>({});
  const [badInput, setBadInput] = useState<ReadonlySet<FieldName>>(() => new Set());

  const change = (field: FieldName, value: string): void => {
    setDraft((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: undefined }));
  };

  const changeRate = (field: FieldName, input: HTMLInputElement): void => {
    // A number input reports text it cannot read as an empty value; `badInput` tells that apart from a field left empty.
    setBadInput((current) => {
      const next = new Set(current);
      if (input.validity.badInput) {
        next.add(field);
      } else {
        next.delete(field);
      }
      return next;
    });
    change(field, input.value);
  };

  const editing = mode.kind === "edit" ? mode.override.modelId : null;
  const optionLabel = useCallback(
    (model: BedrockCatalogueModel): string =>
      model.priceSource === "None" || model.modelId === editing
        ? t("bedrockPrices.dialog.modelOption", { name: model.displayName, id: model.modelId })
        : t("bedrockPrices.dialog.modelOptionPriced", { name: model.displayName, id: model.modelId, source: model.priceSource }),
    [t, editing],
  );

  const submit = async (): Promise<void> => {
    const checked = validateDraft(draft, mode.kind === "edit" ? mode.override.version : undefined, badInput);
    if ("errors" in checked) {
      setErrors(checked.errors);
      onInvalid();
      return;
    }

    const refused = await onSubmit(checked.body);
    if (refused?.field !== undefined && refused.field !== null && (fieldNames as readonly string[]).includes(refused.field)) {
      setErrors({ [refused.field]: "server" });
    }
  };

  const errorFor = (field: FieldName): { id: string; message: string | undefined } => {
    const id = `bedrock-override-${field}`;
    const reason = errors[field];
    return { id, message: reason === undefined ? undefined : t(`bedrockPrices.dialog.errors.${reason}`, { max: MaxRate, maxLength: MaxSourceLength }) };
  };

  const errorLine = (id: string, message: string | undefined): React.JSX.Element | null =>
    message === undefined ? null : (
      <p id={`${id}-error`} className={styles.fieldError}>
        {message}
      </p>
    );

  const field = (name: FieldName, label: string, options: { type?: string; autofocus?: boolean; maxLength?: number } = {}): React.JSX.Element => {
    const { id, message } = errorFor(name);
    const rate = rateFields.includes(name);
    return (
      <div className={`${adminStyles.field} ${styles.dialogField}`}>
        <label htmlFor={id}>{label}</label>
        <input
          id={id}
          type={rate ? "number" : (options.type ?? "text")}
          inputMode={rate ? "decimal" : undefined}
          min={rate ? 0 : undefined}
          max={rate ? MaxRate : undefined}
          step={rate ? "any" : undefined}
          autoComplete="off"
          maxLength={options.maxLength}
          value={draft[name]}
          onChange={(event) => (rate ? changeRate(name, event.target) : change(name, event.target.value))}
          aria-invalid={message === undefined ? undefined : true}
          aria-describedby={message === undefined ? undefined : `${id}-error`}
          data-autofocus={options.autofocus === true ? true : undefined}
        />
        {errorLine(id, message)}
      </div>
    );
  };

  const modelField = (): React.JSX.Element => {
    if (models === null) {
      return field("modelId", t("bedrockPrices.dialog.modelId"), { autofocus: true });
    }
    const { id, message } = errorFor("modelId");
    const offered = offeredModels(models, editing);
    const unpriced = offered.filter((model) => model.priceSource === "None");
    const priced = offered.filter((model) => model.priceSource !== "None");
    // A prefilled or edited model that the list does not hold (it has gone from Bedrock's catalogue) is still offered, so the value is kept.
    const missing = draft.modelId !== "" && !offered.some((model) => model.modelId === draft.modelId) ? draft.modelId : null;
    const option = (model: BedrockCatalogueModel): React.JSX.Element => (
      <option key={model.modelId} value={model.modelId}>
        {optionLabel(model)}
      </option>
    );
    return (
      <div className={`${adminStyles.field} ${styles.dialogField}`}>
        <label htmlFor={id}>{t("bedrockPrices.dialog.modelId")}</label>
        <select
          id={id}
          value={draft.modelId}
          onChange={(event) => change("modelId", event.target.value)}
          aria-invalid={message === undefined ? undefined : true}
          aria-describedby={message === undefined ? undefined : `${id}-error`}
          data-autofocus={mode.kind === "create" && mode.modelId === "" ? true : undefined}
        >
          <option value="">{t("bedrockPrices.dialog.modelChoose")}</option>
          {missing !== null && <option value={missing}>{missing}</option>}
          {unpriced.length > 0 && <optgroup label={t("bedrockPrices.dialog.modelGroupUnpriced")}>{unpriced.map(option)}</optgroup>}
          {priced.length > 0 && <optgroup label={t("bedrockPrices.dialog.modelGroupPriced")}>{priced.map(option)}</optgroup>}
        </select>
        {errorLine(id, message)}
      </div>
    );
  };

  return (
    <Modal title={mode.kind === "create" ? t("bedrockPrices.dialog.createTitle") : t("bedrockPrices.dialog.editTitle", { model: mode.override.modelId })} onClose={onCancel} closeOnBackdropClick={false}>
      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <p className={styles.dialogIntro}>{t("bedrockPrices.dialog.intro")}</p>
        {modelField()}
        <p className={styles.hint}>{t(models === null ? "bedrockPrices.dialog.modelIdHint" : "bedrockPrices.dialog.modelSelectHint")}</p>
        <div className={styles.rateGrid}>
          {field("inputPerMillion", t("bedrockPrices.dialog.input"))}
          {field("outputPerMillion", t("bedrockPrices.dialog.output"))}
          {field("cacheReadPerMillion", t("bedrockPrices.dialog.cacheRead"))}
          {field("cacheWritePerMillion", t("bedrockPrices.dialog.cacheWrite"))}
        </div>
        <p className={styles.hint}>{t("bedrockPrices.dialog.ratesHint", { max: MaxRate })}</p>
        {field("source", t("bedrockPrices.dialog.source"), { maxLength: MaxSourceLength })}
        {field("reviewBy", t("bedrockPrices.dialog.reviewBy"), { type: "date" })}
        <p className={styles.hint}>{t("bedrockPrices.dialog.effectHint")}</p>
        <div className={styles.dialogActions}>
          <button type="button" className="button button-secondary" onClick={onCancel} disabled={busy}>
            {t("bedrockPrices.dialog.cancel")}
          </button>
          <button type="submit" className="button button-primary" disabled={busy}>
            {mode.kind === "create" ? t("bedrockPrices.dialog.create") : t("bedrockPrices.dialog.save")}
          </button>
        </div>
      </form>
    </Modal>
  );
}
